/**
 * 本地存储实现,封装localStorage和sessionStorage
 */
import pkg from "../../package.json";

const { name, version } = pkg;
const NAMESPACE = `${name}__${version}__`.toUpperCase();
const clearExcludes = [];

/**
 * Cache class
 * Construction parameters can be passed into sessionStorage, localStorage
 * @class WebStorage
 */
class WebStorage {
  constructor(storage = sessionStorage, prefix = NAMESPACE) {
    this.storage = storage;
    this.prefix = prefix;
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  getKey(key) {
    return this.prefix + key;
  }

  /**
   * Set cache
   * @param {string} key
   * @param {*} val
   * @returns {void|*}
   */
  set(key, val) {
    if (val === undefined) {
      return this.remove(key);
    }
    this.storage.setItem(this.getKey(key), serialize(val));
    return val;
  }

  /**
   * Read cache
   * @param {string} key
   * @param {*} [def]
   * @returns {*}
   */
  get(key, def) {
    const val = deserialize(this.storage.getItem(this.getKey(key)));
    return val === undefined ? def : val;
  }

  /**
   * Has cache
   * @param {string} key
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== undefined;
  }

  /**
   * Delete cache based on key
   * @param {string} key
   */
  remove(key) {
    this.storage.removeItem(this.getKey(key));
  }

  /**
   * Delete all caches of this instance
   * @param {string[]} [excludes=[]]
   */
  clear(excludes = []) {
    const excludeKeys = new Set([...clearExcludes, ...excludes]);
    Object.keys(this.storage).forEach((key) => {
      if (
        key.startsWith(this.prefix) &&
        !excludeKeys.has(key.slice(this.prefix.length))
      ) {
        this.remove(key.slice(this.prefix.length));
      }
    });
  }

  /**
   * Get all caches of this instance
   * @returns {{}}
   */
  getAll() {
    return Object.keys(this.storage)
      .filter((key) => key.startsWith(this.prefix))
      .reduce((acc, key) => {
        acc[key.slice(this.prefix.length)] = this.get(
          key.slice(this.prefix.length)
        );
        return acc;
      }, {});
  }

  /**
   * @callback callback
   * @param {string} cacheKey
   * @param {*} cacheValue
   */
  /**
   * Traverse all the keys of this instance
   * @param {callback} callback
   */
  forEach(callback) {
    Object.keys(this.storage)
      .filter((key) => key.startsWith(this.prefix))
      .forEach((key) => {
        callback(
          key.slice(this.prefix.length),
          this.get(key.slice(this.prefix.length))
        );
      });
  }
}

function serialize(val) {
  return JSON.stringify(val);
}

function deserialize(val) {
  if (typeof val !== "string") {
    return undefined;
  }
  try {
    return JSON.parse(val);
  } catch (e) {
    return val || undefined;
  }
}

export default new WebStorage(localStorage);
export const session = new WebStorage(sessionStorage);
